import * as THREE from './three'
import { XR_HUD_DISTANCE } from './xrHud'

/**
 * HTML → XR HUD bridge.
 *
 * Immersive sessions hide the page, so existing React/HTML panels are shown in
 * a world-anchored panel instead of being recreated as 3D UI: the real DOM element
 * (kept laid out off screen) is rasterised through an SVG foreignObject into a
 * canvas texture, and XR pointer clicks are forwarded to the real elements at
 * the matching coordinates. The SVG goes through a data: URL, which keeps the
 * canvas untainted for WebGL (a blob: URL taints it in Chromium).
 *
 * Select options use the bridge; text fields focus the real DOM input so Quest
 * presents its native keyboard. Field values are never logged and
 * passwords are masked before rasterisation.
 */

const PX_ANGLE = THREE.MathUtils.degToRad(0.1) // one CSS pixel
const MAX_HEIGHT_ANGLE = THREE.MathUtils.degToRad(70)
const MAX_WIDTH_ANGLE = THREE.MathUtils.degToRad(80)
const TEXTURE_SCALE = 2
const REDRAW_DELAY = 50 // window rAF is paused during immersive sessions
const INHERITED = ['font-family', 'font-size', 'font-weight', 'font-style', 'line-height', 'color', 'letter-spacing', 'text-align', 'box-sizing', 'font-optical-sizing']
const INTERACTIVE = 'button, a[href], input, select, textarea, label, summary, [role="button"], [tabindex]'
const TEXT_TYPES = new Set(['', 'text', 'password', 'email', 'search', 'url', 'tel', 'number'])

export const XR_HTML_PLACEMENT = {
  center: { yaw: 0, pitch: 0 },
  // Always reachable without covering the centre of the view.
  courseButton: { yaw: THREE.MathUtils.degToRad(-22), pitch: THREE.MathUtils.degToRad(-22), followCamera: true },
  bottomRight: { yaw: THREE.MathUtils.degToRad(-22), pitch: THREE.MathUtils.degToRad(-22) },
  bottomCenter: { yaw: 0, pitch: THREE.MathUtils.degToRad(-22) },
  // Slightly raised conversation panel.
  upperCenter: { yaw: 0, pitch: THREE.MathUtils.degToRad(8) },
  popup: { yaw: 0, pitch: 0, layer: 1 },
}

export function isTextField(el) {
  if (el instanceof HTMLTextAreaElement) return true
  return el instanceof HTMLInputElement && TEXT_TYPES.has(el.type)
}

function setNativeValue(el, value) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : el instanceof HTMLSelectElement ? HTMLSelectElement.prototype : HTMLInputElement.prototype
  // The prototype setter bypasses React's value tracker, so React sees the change.
  Object.getOwnPropertyDescriptor(proto, 'value').set.call(el, value)
  el.dispatchEvent(new Event(el instanceof HTMLSelectElement ? 'change' : 'input', { bubbles: true }))
}

function fieldLabel(el) {
  const text = (el.labels?.[0]?.textContent || el.getAttribute('aria-label') || el.name || '').trim()
  return text.slice(0, 60)
}

export class XRHtmlBridge {
  constructor(world, hud) {
    this.world = world
    this.hud = hud
    this.surfaces = new Set()
    this.fonts = new Map()
    this.cssKey = null
    this.css = ''
    this.popup = null
  }

  // Shows the first element child of `host` in the HUD; returns the detach function.
  attach(host, placement = XR_HTML_PLACEMENT.center) {
    const surface = new XRHtmlSurface(this, host, placement)
    this.surfaces.add(surface)
    return () => {
      if (!this.surfaces.delete(surface)) return
      if (this.popup?.owner === surface) this.closePopup()
      surface.destroy()
    }
  }

  activate(el) {
    if (!el.isConnected || el.disabled || el.closest('fieldset:disabled')) return
    if (el instanceof HTMLLabelElement && el.control) el = el.control
    if (el.disabled) return
    if (el instanceof HTMLSelectElement) return this.openSelect(el)
    if (isTextField(el)) {
      this.closePopup()
      el.focus({ preventScroll: true })
      this.ownerOf(el)?.invalidate()
      return
    }
    el.focus?.({ preventScroll: true })
    if (typeof el.click === 'function') el.click()
    else el.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
  }

  ownerOf(el) {
    for (const surface of this.surfaces) if (surface.host !== this.popup?.host && surface.host.contains(el)) return surface
    return null
  }

  openPopup(target, element, placement) {
    this.closePopup()
    const host = document.createElement('div')
    host.style.cssText = 'position:fixed;left:-10000px;top:-10000px;width:1280px;height:4000px;pointer-events:none'
    host.appendChild(element)
    document.body.appendChild(host)
    const popup = { target, host, owner: this.ownerOf(target) }
    this.popup = popup
    popup.detach = this.attach(host, placement)
    target.addEventListener('blur', this.onTargetBlur)
    popup.owner?.invalidate() // focus outline on the edited field
  }

  onTargetBlur = () => {
    // A re-render can replace the field: close only when it left the document.
    setTimeout(() => { if (this.popup && !this.popup.target.isConnected) this.closePopup() }, 0)
  }

  closePopup() {
    const popup = this.popup
    if (!popup) return
    this.popup = null
    popup.target.removeEventListener('blur', this.onTargetBlur)
    popup.detach?.()
    popup.host.remove()
    for (const surface of this.surfaces) surface.invalidate() // drop the focus outline
  }

  openSelect(select) {
    const panel = popupPanel(fieldLabel(select) || 'Select')
    const options = Array.from(select.options)
    const columns = options.length > 24 ? 4 : options.length > 10 ? 2 : 1
    const grid = document.createElement('div')
    grid.style.cssText = `display:grid;grid-template-columns:repeat(${columns},minmax(0,1fr));gap:8px;margin:12px 0`
    options.forEach((option, index) => {
      const item = keyButton(option.textContent, () => {
        this.closePopup()
        if (select.isConnected && !select.disabled && select.selectedIndex !== index) setNativeValue(select, option.value)
      })
      item.disabled = option.disabled
      item.style.textAlign = 'left'
      if (index === select.selectedIndex) item.style.background = '#3a5a80'
      grid.appendChild(item)
    })
    panel.style.width = `${columns * 240}px`
    panel.append(grid, keyButton('Cancel', () => this.closePopup()))
    this.openPopup(select, panel, XR_HTML_PLACEMENT.popup)
  }

  // Same-origin page CSS with font files inlined: an SVG image cannot fetch.
  async pageCSS() {
    const sheets = Array.from(document.styleSheets)
    let key = `${sheets.length}`
    const texts = []
    for (const sheet of sheets) {
      try {
        const rules = sheet.cssRules
        key += `:${rules.length}`
        texts.push(rules)
      } catch {
        /* cross-origin sheet */
      }
    }
    if (key === this.cssKey) return this.css
    let css = ''
    for (const rules of texts) for (const rule of rules) css += rule.cssText + '\n'
    const urls = new Set()
    css.replace(/@font-face[^}]*}/g, block => {
      for (const match of block.matchAll(/url\(["']?([^"')]+)["']?\)/g)) if (!match[1].startsWith('data:')) urls.add(match[1])
      return block
    })
    for (const url of urls) {
      if (!this.fonts.has(url)) this.fonts.set(url, await fontDataURL(url))
      const data = this.fonts.get(url)
      if (data) css = css.split(url).join(data)
    }
    this.cssKey = key
    this.css = css.replace(/]]>/g, '')
    return this.css
  }

  reset() {
    this.closePopup()
  }

  destroy() {
    this.closePopup()
    for (const surface of this.surfaces) surface.destroy()
    this.surfaces.clear()
  }
}

async function fontDataURL(url) {
  try {
    const buffer = await (await fetch(url)).arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000))
    return `data:font/woff2;base64,${btoa(binary)}`
  } catch {
    return null // falls back to the next font in the stack
  }
}

const KEY_STYLE = 'padding:10px 12px;border-radius:8px;background:#24364b;border:1px solid #526477;color:white;font:inherit;font-size:20px;min-width:0'

function popupPanel(title) {
  const panel = document.createElement('div')
  panel.style.cssText = 'padding:16px;border-radius:12px;border:1px solid #526477;background:#0b121d;color:white;font-family:Rubik,sans-serif;font-size:16px;box-sizing:border-box'
  const heading = document.createElement('strong')
  heading.textContent = title
  panel.appendChild(heading)
  return panel
}

function keyButton(label, onClick, grow = 1) {
  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = label
  button.style.cssText = `${KEY_STYLE};flex:${grow} 1 0`
  button.addEventListener('click', onClick)
  return button
}

export class XRHtmlSurface {
  constructor(bridge, host, placement) {
    this.isHudSurface = true
    this.bridge = bridge
    this.host = host
    this.parent = null
    this.rect = null
    this.drawing = false
    this.dirty = false
    this.timer = null
    this.proxies = new WeakMap()
    this.hovered = null
    this.canvas = document.createElement('canvas')
    this.material = new THREE.MeshBasicMaterial({ transparent: true, toneMapped: false })
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.material)
    this.mesh.visible = false // until the first rasterisation
    this.observer = new MutationObserver(this.invalidate)
    this.observer.observe(host, { subtree: true, childList: true, attributes: true, characterData: true })
    host.addEventListener('input', this.invalidate, true)
    host.addEventListener('change', this.invalidate, true)
    this.hide = bridge.hud.show(this, placement)
    this.invalidate()
  }

  invalidate = () => {
    if (this.destroyed || this.timer) return
    this.timer = setTimeout(() => {
      this.timer = null
      this.draw()
    }, REDRAW_DELAY)
  }

  async draw() {
    if (this.drawing) {
      this.dirty = true
      return
    }
    this.drawing = true
    try {
      const root = this.host.firstElementChild
      if (!root) {
        this.mesh.visible = false
        return
      }
      const css = await this.bridge.pageCSS()
      const rect = root.getBoundingClientRect()
      if (!rect.width || !rect.height) {
        this.mesh.visible = false
        return
      }
      const image = await rasterize(root, rect, css, this.bridge.popup?.target)
      if (this.destroyed) return
      const width = Math.round(rect.width * TEXTURE_SCALE)
      const height = Math.round(rect.height * TEXTURE_SCALE)
      if (!this.texture || this.canvas.width !== width || this.canvas.height !== height) {
        // A WebGL texture keeps its first size: resize with a new one.
        this.texture?.dispose()
        this.canvas = document.createElement('canvas')
        this.canvas.width = width
        this.canvas.height = height
        this.texture = new THREE.CanvasTexture(this.canvas)
        this.texture.colorSpace = THREE.SRGBColorSpace
        this.texture.anisotropy = this.bridge.world.graphics?.maxAnisotropy || 1
        this.material.map = this.texture
        this.material.needsUpdate = true
      }
      const ctx = this.canvas.getContext('2d')
      ctx.clearRect(0, 0, width, height)
      ctx.drawImage(image, 0, 0, width, height)
      this.texture.needsUpdate = true
      if (!this.rect || this.rect.width !== rect.width || this.rect.height !== rect.height) {
        const angle = Math.min(PX_ANGLE, MAX_HEIGHT_ANGLE / rect.height, MAX_WIDTH_ANGLE / rect.width)
        const metres = 2 * XR_HUD_DISTANCE * Math.tan(angle / 2)
        this.mesh.geometry.dispose()
        this.mesh.geometry = new THREE.PlaneGeometry(rect.width * metres, rect.height * metres)
      }
      this.rect = { width: rect.width, height: rect.height }
      this.mesh.visible = true
    } catch (err) {
      console.error('[xr-html] rasterisation failed', err?.name || err)
    } finally {
      this.drawing = false
      if (this.dirty && !this.destroyed) {
        this.dirty = false
        this.invalidate()
      }
    }
  }

  // XR pointer path: the element under the ray, as a pointer node.
  resolveHit(hit) {
    const root = this.host.firstElementChild
    if (!root || !hit.uv) return this
    const rect = root.getBoundingClientRect()
    const x = rect.left + hit.uv.x * rect.width
    const y = rect.top + (1 - hit.uv.y) * rect.height
    const el = elementAt(root, x, y)?.closest(INTERACTIVE)
    if (!el || !root.contains(el) || el.disabled) return this
    let proxy = this.proxies.get(el)
    if (!proxy) {
      proxy = {
        parent: this,
        cursor: 'pointer',
        onPointerDown: e => e.stopPropagation(),
        onPointerUp: () => this.bridge.activate(el),
        onPointerEnter: () => el.dispatchEvent(new PointerEvent('pointerover', { bubbles: true })),
        onPointerLeave: () => el.dispatchEvent(new PointerEvent('pointerout', { bubbles: true })),
      }
      this.proxies.set(el, proxy)
    }
    return proxy
  }

  destroy() {
    if (this.destroyed) return
    this.destroyed = true
    clearTimeout(this.timer)
    this.observer.disconnect()
    this.host.removeEventListener('input', this.invalidate, true)
    this.host.removeEventListener('change', this.invalidate, true)
    this.bridge.world.pointer?.cancelForNode?.(this)
    this.hide()
    this.mesh.geometry.dispose()
    this.material.dispose()
    this.texture?.dispose()
  }
}

// Deepest element containing the point; later siblings are drawn on top.
export function elementAt(root, x, y) {
  let found = null
  for (const el of [root, ...root.querySelectorAll('*')]) {
    const r = el.getBoundingClientRect()
    if (r.width && r.height && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) found = el
  }
  return found
}

async function rasterize(root, rect, css, focused) {
  const clone = root.cloneNode(true)
  const source = [root, ...root.querySelectorAll('*')]
  const copy = [clone, ...clone.querySelectorAll('*')]
  for (let i = 0; i < source.length; i++) {
    const a = source[i]
    const b = copy[i]
    if (a instanceof HTMLInputElement) {
      if (a.type === 'checkbox' || a.type === 'radio') b.toggleAttribute('checked', a.checked)
      // Masked before serialisation: the password never enters the image.
      else b.setAttribute('value', a.type === 'password' ? '•'.repeat(a.value.length) : a.value)
    } else if (a instanceof HTMLTextAreaElement) {
      b.textContent = a.value
    } else if (a instanceof HTMLSelectElement) {
      Array.from(b.options).forEach((option, index) => option.toggleAttribute('selected', index === a.selectedIndex))
    } else if (a instanceof HTMLCanvasElement) {
      try {
        const img = document.createElement('img')
        img.src = a.toDataURL()
        img.style.cssText = a.style.cssText
        b.replaceWith(img)
      } catch {
        /* tainted canvas stays blank */
      }
    }
    // A clone loses scroll offsets: shift the content instead (e.g. latest chat messages).
    if (a.scrollTop > 0 && b.firstElementChild && a.firstElementChild) {
      const margin = parseFloat(getComputedStyle(a.firstElementChild).marginTop) || 0
      b.firstElementChild.style.marginTop = `${margin - a.scrollTop}px`
      b.style.overflow = 'hidden'
    }
    if (a === focused) b.style.outline = '3px solid #4da3ff'
  }
  const computed = getComputedStyle(root)
  for (const name of INHERITED) clone.style.setProperty(name, computed.getPropertyValue(name))
  Object.assign(clone.style, {
    position: 'relative', // contains absolutely positioned children
    left: 'auto',
    top: 'auto',
    right: 'auto',
    bottom: 'auto',
    transform: 'none',
    margin: '0',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    maxHeight: 'none',
    overflow: 'hidden',
  })
  const xml = new XMLSerializer().serializeToString(clone)
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${rect.width}" height="${rect.height}">` +
    `<style><![CDATA[${css}]]></style>` +
    `<foreignObject x="0" y="0" width="100%" height="100%">${xml}</foreignObject></svg>`
  const image = new Image()
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  await image.decode()
  return image
}
